import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box, Typography, CircularProgress, Pagination, Button, TextField,
  Paper, Table, TableBody, TableHead, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Chip, Stack, Badge, Popover, MenuItem,
  Select, FormControl, InputLabel, Divider, Collapse, InputAdornment,
  Switch, FormControlLabel, ListItemText,
  ToggleButtonGroup, ToggleButton, useMediaQuery, Menu, ListItemIcon, Snackbar, Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import UndoIcon from '@mui/icons-material/Undo';
import pako from 'pako';
import { useAuth } from '../authContext';

import { getProducts, getProductCount, getApiKey, deleteProduct, saveProductSpecs, saveBodyHtml, saveProductShopifyCategory, saveProductEbayCategory, saveProductQuantity, saveProductDimensions, saveProductFields, isSkuUnique, getCustomerDoc, mapProductDoc, getCategoryConfig } from '../../databaseManager';
import { postProductToChannel, updateProductChannel, deleteProductFromChannel, retryProduct, regenerateProduct } from '../api';
import { SHOPIFY_CATEGORY_OPTIONS } from './shopifyCategoryOptions';
import { PRODUCTS_ENDPOINT } from '../api';
import { generateBodyHtml } from './bodyHtmlGenerator';
import { useProductConfig } from './useProductConfig';
import { useSaveProductConfig } from './useSaveProductConfig';
import { DEFAULT_CATEGORY_CONFIGS } from './defaultCategoryConfigs';
import { normalizeCategoryKey, conditionToValueMap } from '../productEditConstants';
import InlineEditCell from './InlineEditCell';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import BodyHtmlDrawer from './BodyHtmlDrawer';
import ProductPhotoManager from './ProductPhotoManager';
import DraggableColumnHeader from './DraggableColumnHeader';
import EditBar from './EditBar';
import FilterDialog, { countActiveFilters, inNumberRange, inDayRange } from '../filters/FilterDialog';
import PriceConditionDialog from './PriceConditionDialog';
import MarkAsSoldDialog from './MarkAsSoldDialog';
import ListingChannelsDialog from './ListingChannelsDialog';
import MissingEbayFieldsDialog from './MissingEbayFieldsDialog';
import { DetailToolbar, MarketValueRow, OtherFieldsSection, ListingActionsSection } from './ProductDetailSections';
import {
  isListedOnChannel, isListedAnywhere, parsePriceValue, storedDateDay, channelErrorText,
  missingRequiredEbayFields, missingEtsySettings, buildClipboardText, didFailProcessing,
  formatCurrency, channelLabel, formatStoredDate,
  tierForSpecs, marketValueFor, isGradedCard, CONDITION_TIER_FIELDS, GRADE_REPRICE_FIELDS,
} from './productUtils';

const PAGE_SIZE_OPTIONS = [10, 25, 100, 250];

// Grid (cards) vs. table (spreadsheet) layout — remembered per browser.
// Table view is desktop-only; narrow screens always get the card grid.
const VIEW_MODE_STORAGE_KEY = 'productListViewMode';
function readStoredViewMode() {
  try {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'table' ? 'table' : 'grid';
  } catch {
    return 'grid';
  }
}

// Placeholder card data for a product whose doc doesn't exist yet (or was just
// created) — mirrors the shape mapProductDoc produces so the card renders the
// same "Generating…" spinners it would for any other in-progress product.
function buildPendingPlaceholder({ productUri, previewUrl }) {
  return {
    productUri,
    price: 'Unknown',
    mainImages: [],
    thumbnailUrls: previewUrl ? [previewUrl] : [],
    mainImage: previewUrl || null,
    created: new Date().toISOString(),
    quantity: 1,
    productReady: false,
    finishedProcessing: false,
    needsImages: false,
    specs: {},
    sku: '',
    validatedTitle: null,
    ebayData: null,
    shopifyData: null,
    squareData: null,
  };
}

const getCurrentDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function getFieldValue(product, fieldPath) {
  if (!fieldPath) return undefined;
  if (fieldPath.startsWith('consolidatedData.')) {
    const key = fieldPath.slice('consolidatedData.'.length);
    return product.specs?.[key];
  }
  const parts = fieldPath.split('.');
  let val = product;
  for (const part of parts) { val = val?.[part]; }
  return val;
}

// Overlays a single uncommitted edit onto a product, for local preview only.
function setFieldValue(product, fieldPath, newValue) {
  if (fieldPath.startsWith('consolidatedData.')) {
    const key = fieldPath.slice('consolidatedData.'.length);
    return { ...product, specs: { ...(product.specs || {}), [key]: newValue } };
  }
  const parts = fieldPath.split('.');
  if (parts.length === 1) return { ...product, [parts[0]]: newValue };
  const [head, ...rest] = parts;
  return { ...product, [head]: { ...(product[head] || {}), [rest.join('.')]: newValue } };
}

const FILTER_PAGE_SIZE = 200;

const MARKETPLACE_OPTIONS = [
  { key: 'ebay', label: 'eBay' },
  { key: 'shopify', label: 'Shopify' },
  { key: 'etsy', label: 'Etsy' },
  { key: 'googleMerchant', label: 'Google Merchant' },
  { key: 'square', label: 'Square' },
];

// All / Listed / Unlisted — same segments as the iOS inventory list.
const SEGMENTS = [
  { key: 'all', label: 'All' },
  { key: 'listed', label: 'Listed' },
  { key: 'unlisted', label: 'Unlisted' },
];

// Channels a product can be posted to / updated on from the list.
const POSTABLE_CHANNELS = ['shopify', 'ebay', 'square', 'etsy'];

// Normalized form of a cell value, for deciding whether an edit actually changed anything.
const comparable = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v).replace(/^\$/, '').trim());

// Builds a nested object from a dot path so setDoc(merge) only touches that leaf.
function nestedPatch(fieldPath, value) {
  return fieldPath.split('.').reduceRight((acc, key) => ({ [key]: acc }), value);
}

function mergeDeep(a, b) {
  const out = { ...a };
  Object.entries(b).forEach(([k, v]) => {
    out[k] = v && typeof v === 'object' && !Array.isArray(v) && out[k] && typeof out[k] === 'object'
      ? mergeDeep(out[k], v)
      : v;
  });
  return out;
}


const EMPTY_FILTERS = {
  listedOn: [],
  brand: '',
  model: '',
  category: '',
  condition: '',
  priceMin: '',
  priceMax: '',
  qtyMin: '',
  qtyMax: '',
  createdStart: '',
  createdEnd: '',
  updatedStart: '',
  updatedEnd: '',
  tagsNotes: '',
};

function ChannelBadge({ label, active }) {
  return (
    <Chip
      label={label}
      size="small"
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
    />
  );
}

export default function ProductListV3() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer } = useParams();
  const { customer: customerData } = useAuth();

  // ── Layout (grid vs. table) ─────────────────────────────────────────────────
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const handleViewModeChange = useCallback((_, next) => {
    if (!next) return;
    setViewMode(next);
    try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);
  const isTableView = isDesktop && viewMode === 'table';

  // ── Integration detection ───────────────────────────────────────────────────
  const hasShopify = useMemo(
    () => customerData?.destinations?.some(d => d.type === 1),
    [customerData]
  );
  const hasEbay = useMemo(
    () => customerData?.destinations?.some(d => d.type === 2),
    [customerData]
  );
  const hasSquare = useMemo(
    () => !!customerData?.squareSettings,
    [customerData]
  );
  const hasEtsy = useMemo(
    () => customerData?.destinations?.some(d => d.type === 5),
    [customerData]
  );
  const hasChannel = useMemo(
    () => ({ shopify: hasShopify, ebay: hasEbay, square: hasSquare, etsy: hasEtsy }),
    [hasShopify, hasEbay, hasSquare, hasEtsy]
  );

  // Inventory totals maintained by the backend (customer.itemCount/unsoldValue/
  // listedItemCount/listedUnsoldValue). Read fresh here rather than from the
  // auth context, which only loads the customer doc once at sign-in.
  const [inventoryTotals, setInventoryTotals] = useState(null);
  useEffect(() => {
    if (!customer) return;
    getCustomerDoc(customer)
      .then((c) => c && setInventoryTotals({
        itemCount: c.itemCount ?? null,
        unsoldValue: c.unsoldValue ?? null,
        listedItemCount: c.listedItemCount ?? null,
        listedUnsoldValue: c.listedUnsoldValue ?? null,
      }))
      .catch(console.error);
  }, [customer]);

  // ── Data state ──────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(isDesktop ? 250 : 10);
  const [cursors, setCursors] = useState([]);
  const [lastSnap, setLastSnap] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // ── Filter / sort / search state ────────────────────────────────────────────
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [segment, setSegment] = useState('all');
  const [sortDir, setSortDir] = useState('desc');
  const [seenCategories, setSeenCategories] = useState([]);
  const [seenBrands, setSeenBrands] = useState([]);
  const [seenModels, setSeenModels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  // ── Filter dialog ────────────────────────────────────────────────────────────
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const filterFields = useMemo(() => [
    { type: 'multiselect', key: 'listedOn', label: 'Listed on', anyLabel: 'Any marketplace',
      options: MARKETPLACE_OPTIONS.map((o) => ({ value: o.key, label: o.label })) },
    { type: 'autocomplete', key: 'brand', label: 'Brand', placeholder: 'Select brand', options: seenBrands },
    { type: 'autocomplete', key: 'model', label: 'Model', placeholder: 'Select model', options: seenModels },
    { type: 'autocomplete', key: 'category', label: 'Category', placeholder: 'Select category', options: seenCategories },
    { type: 'select', key: 'condition', label: 'Condition', placeholder: 'Select condition',
      options: Object.entries(conditionToValueMap).map(([value, label]) => ({ value, label })) },
    { type: 'range', label: 'Price range', minKey: 'priceMin', maxKey: 'priceMax', prefix: '$' },
    { type: 'range', label: 'Quantity', minKey: 'qtyMin', maxKey: 'qtyMax' },
    { type: 'dateRange', label: 'Created date', startKey: 'createdStart', endKey: 'createdEnd' },
    { type: 'dateRange', label: 'Updated date', startKey: 'updatedStart', endKey: 'updatedEnd' },
    { type: 'text', key: 'tagsNotes', label: 'Tags / Notes', placeholder: 'Search tags or notes…' },
  ], [seenBrands, seenModels, seenCategories]);

  // ── Export popover ──────────────────────────────────────────────────────────
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());

  const hasActiveFilter = activeFilterCount > 0 || !!searchQuery || segment !== 'all';

  // A single-item upload can land us here before the product's Firestore doc
  // even exists yet (see uploader.js) — captured once on mount so a later
  // in-place navigation (e.g. paginating) doesn't keep re-triggering this.
  const [pendingUpload] = useState(() => location.state?.pendingProductId
    ? { productUri: location.state.pendingProductId, previewUrl: location.state.pendingPreviewUrl || null }
    : null);
  const pendingUploadRef = useRef(pendingUpload ? buildPendingPlaceholder(pendingUpload) : null);

  // Merge the pending upload's placeholder/live card into a freshly-fetched
  // page-1 list, unless the server-side fetch already picked it up on its own.
  const mergeInPendingUpload = useCallback((list) => {
    if (!pendingUploadRef.current || page !== 1 || hasActiveFilter) return list;
    if (list.some((p) => p.productUri === pendingUploadRef.current.productUri)) return list;
    return [pendingUploadRef.current, ...list];
  }, [page, hasActiveFilter]);

  // Show the placeholder the instant we land here, before the real fetch
  // (or the doc itself) exists yet.
  useEffect(() => {
    if (!pendingUploadRef.current) return;
    setProducts((prev) => (
      prev.some((p) => p.productUri === pendingUploadRef.current.productUri) ? prev : [pendingUploadRef.current, ...prev]
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live-update that placeholder as soon as the product doc is created and as
  // it fills in (title/price/etc.), instead of waiting on the 8s polling loop.
  useEffect(() => {
    if (!pendingUpload) return;
    const ref = doc(db, `customers/${customer}/products/${pendingUpload.productUri}`);
    const unsub = onSnapshot(ref, (snap) => {
      const mapped = mapProductDoc(snap, { customer }) || {};
      mapped.productUri = pendingUpload.productUri;
      if (!mapped.thumbnailUrls?.length && pendingUpload.previewUrl) {
        mapped.thumbnailUrls = [pendingUpload.previewUrl];
      }
      pendingUploadRef.current = mapped;
      setProducts((prev) => {
        const exists = prev.some((p) => p.productUri === pendingUpload.productUri);
        return exists
          ? prev.map((p) => (p.productUri === pendingUpload.productUri ? mapped : p))
          : [mapped, ...prev];
      });
      // Fully processed — nothing left to watch live; the existing 8s
      // pending-recheck loop covers any further changes like any other product.
      if (mapped.productReady) unsub();
    });
    return () => unsub();
  }, [pendingUpload, customer]);

  // ── Column config ───────────────────────────────────────────────────────────
  const { config, loading: configLoading } = useProductConfig(customer, 'default');
  const { save: saveConfig } = useSaveProductConfig(customer, 'default');
  const [localConfig, setLocalConfig] = useState(null);
  useEffect(() => { setLocalConfig(config); }, [config]);

  const visibleColumns = useMemo(() => {
    return (localConfig?.columns || []).map((c) => (
      // SKU used to be read-only in the default config, and saved configs keep
      // that flag — it's editable now (locked per-row once on eBay instead).
      c.id === 'sku' ? { ...c, readOnly: false } : c
    )).filter((c) => {
      if (c.visible === false) return false;
      if ((c.id === 'shopify' || c.id === 'shopifyCategory') && !hasShopify) return false;
      if (c.shopifyOnly && !hasShopify) return false;
      if ((c.id === 'ebay' || c.id === 'ebayCategory') && !hasEbay) return false;
      if (c.id === 'square' && !hasSquare) return false;
      if (c.id === 'etsy' && !hasEtsy) return false;
      return true;
    });
  }, [localConfig, hasShopify, hasEbay, hasSquare, hasEtsy]);

  // The image is shown large in the card header, and title/price are shown as
  // the card's headline — no need to repeat the image row in the detail list.
  const detailColumns = useMemo(
    () => visibleColumns.filter((c) => c.type !== 'image'),
    [visibleColumns]
  );
  const imageCol = useMemo(() => visibleColumns.find((c) => c.type === 'image'), [visibleColumns]);
  // Spec fields already shown as a configured row — the rest go under "More details".
  const coveredFieldPaths = useMemo(
    () => new Set(detailColumns.map((c) => c.fieldPath).filter(Boolean)),
    [detailColumns]
  );
  const titleCol = useMemo(
    () => visibleColumns.find((c) => c.id === 'title') || visibleColumns.find((c) => /title/i.test(c.fieldPath || '')),
    [visibleColumns]
  );
  const priceCol = useMemo(
    () => visibleColumns.find((c) => c.id === 'price') || visibleColumns.find((c) => c.type === 'currency'),
    [visibleColumns]
  );
  const skuCol = useMemo(() => visibleColumns.find((c) => c.id === 'sku'), [visibleColumns]);

  // ── Local edits (per-product dirty specs, already saved to the backend) ────
  const [dirtySpecs, setDirtySpecs] = useState({});
  // ── Uncommitted edits, staged until the floating bar's "Save" is clicked ───
  // { [productUri]: { product, fields: { [fieldPath]: { column, value } } } }
  // The product snapshot is kept with its edits so they survive paging,
  // filtering or searching away from that product before saving.
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [savingUris, setSavingUris] = useState(new Set());
  const [pendingChannels, setPendingChannels] = useState(new Set());
  const [enhancingProducts, setEnhancingProducts] = useState(new Set());
  const [regeneratingUris, setRegeneratingUris] = useState(new Set());
  const [toast, setToast] = useState(null); // { severity, message }

  // ── Dialog state ────────────────────────────────────────────────────────────
  const [priceDialogProduct, setPriceDialogProduct] = useState(null);
  const [soldDialogProduct, setSoldDialogProduct] = useState(null);
  const [listingDialogProduct, setListingDialogProduct] = useState(null);
  const [missingEbay, setMissingEbay] = useState(null); // { product, fields }
  const [rowMenu, setRowMenu] = useState(null); // { anchorEl, product }

  // ── Expand/collapse state ───────────────────────────────────────────────────
  const [expandedUris, setExpandedUris] = useState(new Set());
  const toggleExpand = useCallback((uri) => {
    setExpandedUris((prev) => {
      const next = new Set(prev);
      next.has(uri) ? next.delete(uri) : next.add(uri);
      return next;
    });
  }, []);

  // ── Body HTML drawer ────────────────────────────────────────────────────────
  const [drawerProduct, setDrawerProduct] = useState(null);

  // ── Delete dialog ───────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteEverywhere, setDeleteEverywhere] = useState(false);

  // ── Channel action error dialog ─────────────────────────────────────────────
  const [channelError, setChannelError] = useState(null);

  // ── Load product count ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasActiveFilter) {
      getProductCount(customer).then(setTotalItems).catch(console.error);
    }
  }, [customer, hasActiveFilter]);

  // ── Load page of products ──────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    if (hasActiveFilter) {
      getProducts(FILTER_PAGE_SIZE, null, customer, '', (list) => {
        let filtered = list;
        if (filters.listedOn.length > 0) {
          filtered = filtered.filter(p => filters.listedOn.some((k) => isListedOnChannel(p, k)));
        }
        if (segment === 'listed') filtered = filtered.filter(isListedAnywhere);
        if (segment === 'unlisted') filtered = filtered.filter(p => !isListedAnywhere(p));
        // Case-insensitive "contains" rather than exact equality — these can be
        // free-typed values, not just picks from the dropdown (same as iOS).
        const specContains = (p, key, needle) => (p.specs?.[key] || '').toLowerCase().includes(needle.toLowerCase());
        if (filters.brand) filtered = filtered.filter(p => specContains(p, 'Brand', filters.brand));
        if (filters.model) filtered = filtered.filter(p => specContains(p, 'Model', filters.model));
        if (filters.category) filtered = filtered.filter(p => specContains(p, 'Category', filters.category));
        if (filters.condition) filtered = filtered.filter(p => (p.specs?.Condition || '') === filters.condition);
        filtered = filtered.filter(p => inNumberRange(parsePriceValue(p.price), filters.priceMin, filters.priceMax));
        filtered = filtered.filter(p => inNumberRange(p.quantity, filters.qtyMin, filters.qtyMax));
        filtered = filtered.filter(p => inDayRange(storedDateDay(p.created), filters.createdStart, filters.createdEnd));
        filtered = filtered.filter(p => inDayRange(storedDateDay(p.updated), filters.updatedStart, filters.updatedEnd));
        if (filters.tagsNotes) {
          const needle = filters.tagsNotes.toLowerCase();
          filtered = filtered.filter(p => {
            const tagsMatch = (p.tags || []).some(t => (t || '').toLowerCase().includes(needle));
            const notesMatch = (p.notes || '').toLowerCase().includes(needle);
            return tagsMatch || notesMatch;
          });
        }
        if (searchQuery) {
          const needle = searchQuery.toLowerCase();
          filtered = filtered.filter(p => {
            const title = (p.specs?.Title || p.title || '').toLowerCase();
            const sku = (p.sku || '').toLowerCase();
            return title.includes(needle) || sku.includes(needle);
          });
        }
        if (sortDir === 'asc') filtered = [...filtered].reverse();
        setProducts(filtered);
        setTotalItems(filtered.length);
        setLoading(false);
      }, false, { sortDir });
    } else {
      getProducts(pageSize, lastSnap, customer, '', (list, lastVisible) => {
        setProducts(mergeInPendingUpload(list));
        setCursors((prev) => {
          const updated = [...prev];
          updated[page - 1] = lastVisible;
          return updated;
        });
        setLoading(false);
      }, false, { sortDir });
    }
  }, [page, pageSize, customer, hasActiveFilter, filters, searchQuery, sortDir, segment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect unique categories/brands/models from loaded products
  useEffect(() => {
    setSeenCategories(prev => {
      const set = new Set(prev);
      products.forEach(p => { if (p.specs?.Category) set.add(p.specs.Category); });
      return Array.from(set).sort();
    });
    setSeenBrands(prev => {
      const set = new Set(prev);
      products.forEach(p => { if (p.specs?.Brand) set.add(p.specs.Brand); });
      return Array.from(set).sort();
    });
    setSeenModels(prev => {
      const set = new Set(prev);
      products.forEach(p => { if (p.specs?.Model) set.add(p.specs.Model); });
      return Array.from(set).sort();
    });
  }, [products]);

  // Products can still be mid-processing (title/price/category not yet
  // written) when this page was fetched. The initial load is a one-time
  // getDocs, not a live listener, so without this the "Generating…"
  // spinners would stay stuck until the user reloads the whole page.
  useEffect(() => {
    if (loading) return;
    const pendingUris = products.filter(p => !p.productReady).map(p => p.productUri);
    if (pendingUris.length === 0) return;

    const t = setTimeout(() => {
      pendingUris.forEach((uri) => {
        getProducts(1, null, customer, '', ([updated]) => {
          if (!updated) return;
          setProducts((prev) => prev.map((p) => p.productUri === updated.productUri ? updated : p));
        }, false, { productUri: uri });
      });
    }, 8000);
    return () => clearTimeout(t);
  }, [products, loading, customer]);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
    setLastSnap(newPage === 1 ? null : (cursors[newPage - 2] || null));
  }, [cursors]);

  const handlePageSizeChange = useCallback((e) => {
    setPageSize(e.target.value);
    setPage(1);
    setLastSnap(null);
    setCursors([]);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    setLastSnap(null);
    setCursors([]);
  }, [filters, searchQuery, sortDir, segment]);

  // Saved per-category body HTML templates (bullets like Entrupy live here, not in the code defaults).
  const [savedTemplates, setSavedTemplates] = useState({});
  const savedTemplatesRef = useRef({});
  useEffect(() => {
    if (!customer) return undefined;
    let cancelled = false;
    Promise.all(Object.keys(DEFAULT_CATEGORY_CONFIGS).map(async (key) => {
      const saved = await getCategoryConfig(customer, key);
      return [key, saved?.bodyHtmlTemplate || null];
    })).then((entries) => {
      if (cancelled) return;
      const map = Object.fromEntries(entries.filter(([, t]) => t));
      savedTemplatesRef.current = map;
      setSavedTemplates(map);
    });
    return () => { cancelled = true; };
  }, [customer]);

  // Pick the body HTML template for a product: prefer saved config, fall back to category default.
  // Category comes from the confirmed Shopify category first (same as generateBodyHtml), then specs.
  const getBodyHtmlTemplate = useCallback((product) => {
    const catKey = normalizeCategoryKey(product.shopifyCategory?.fullName || product.specs?.Category || '');
    const saved = savedTemplatesRef.current[catKey] || savedTemplates[catKey];
    if (saved) return saved;
    const catDefault = DEFAULT_CATEGORY_CONFIGS[catKey] || DEFAULT_CATEGORY_CONFIGS.default;
    return catDefault.bodyHtmlTemplate;
  }, [savedTemplates]);

  // Re-reads one product doc into the list. The server copy now has every
  // saved spec, so the local dirty-spec overlay for it is dropped too.
  const refreshProduct = useCallback((productUri) => new Promise((resolve) => {
    getProducts(1, null, customer, '', ([updated]) => {
      if (updated) {
        setProducts((prev) => prev.map((p) => p.productUri === updated.productUri ? updated : p));
        setDirtySpecs((prev) => {
          if (!prev[productUri]) return prev;
          const next = { ...prev };
          delete next[productUri];
          return next;
        });
      }
      resolve(updated || null);
    }, false, { productUri });
  }), [customer]);

  // ── Inline cell editing (staged locally until the floating bar's Save) ─────
  // Any editable field can be clicked straight into edit mode; edits collect
  // here across products, and one Save/Discard bar commits or drops them all.
  const handleFieldEdit = useCallback((product, column, newValue) => {
    setPendingEdits((prev) => {
      const entry = prev[product.productUri];
      const fields = { ...(entry?.fields || {}) };
      const saved = { ...product, specs: { ...(product.specs || {}), ...(dirtySpecs[product.productUri] || {}) } };
      // Editing a field back to its saved value un-stages it.
      if (comparable(getFieldValue(saved, column.fieldPath)) === comparable(newValue)) {
        delete fields[column.fieldPath];
      } else {
        fields[column.fieldPath] = { column, value: newValue };
      }
      // A Condition / Card Condition edit moves the price to that condition's
      // tier (same PRICE_TIERS as the "Suggested value by condition" dialog),
      // staged alongside it so the seller sees it and can still override or
      // discard it. Graded cards are priced by grade, not tier — skipped.
      const editedSpec = column.fieldPath.startsWith('consolidatedData.') ? column.fieldPath.slice('consolidatedData.'.length) : null;
      if (CONDITION_TIER_FIELDS.includes(editedSpec)) {
        const pendingSpecs = Object.fromEntries(Object.values(fields)
          .filter(({ column: c }) => c.fieldPath.startsWith('consolidatedData.'))
          .map(({ column: c, value }) => [c.fieldPath.slice('consolidatedData.'.length), value]));
        const nextSpecs = { ...saved.specs, ...pendingSpecs };
        const marketValue = marketValueFor(saved);
        if (!isGradedCard(nextSpecs) && marketValue > 0) {
          const tierPriceColumn = priceCol?.fieldPath === 'consolidatedData.Price' ? priceCol : { id: 'price', label: 'Price', fieldPath: 'consolidatedData.Price', type: 'currency' };
          const tieredPrice = (Math.round(marketValue * tierForSpecs(nextSpecs).percentage * 100) / 100).toFixed(2);
          if (comparable(getFieldValue(saved, tierPriceColumn.fieldPath)) === comparable(tieredPrice)) {
            delete fields[tierPriceColumn.fieldPath];
          } else {
            fields[tierPriceColumn.fieldPath] = { column: tierPriceColumn, value: tieredPrice };
          }
        }
      }
      const next = { ...prev };
      if (Object.keys(fields).length > 0) next[product.productUri] = { product, fields };
      else delete next[product.productUri];
      return next;
    });
  }, [dirtySpecs, priceCol]);

  const handleDiscardEdits = useCallback((productUri) => {
    setPendingEdits((prev) => {
      const next = { ...prev };
      delete next[productUri];
      return next;
    });
  }, []);

  const handleDiscardAll = useCallback(() => setPendingEdits({}), []);

  // Persists one product's staged edits; returns the patch to apply locally.
  // Throws (before writing anything) when the SKU edit is invalid.
  const saveProductEdits = useCallback(async (product, fields) => {
    const specEdits = {};
    const otherEdits = [];
    Object.values(fields).forEach(({ column, value }) => {
      if (column.fieldPath.startsWith('consolidatedData.')) {
        specEdits[column.fieldPath.slice('consolidatedData.'.length)] = value;
      } else {
        otherEdits.push({ column, value });
      }
    });

    let sku;
    if (otherEdits.some(({ column }) => column.fieldPath === 'sku')) {
      if (isListedOnChannel(product, 'ebay')) throw new Error('SKU cannot be edited after posting to eBay.');
      // Dots aren't allowed in SKUs, same as the iOS SKU field.
      sku = String(otherEdits.find(({ column }) => column.fieldPath === 'sku').value || '').replace(/\./g, '').trim();
      if (sku && !(await isSkuUnique(customer, sku, product.productUri))) {
        throw new Error(`SKU "${sku}" is already used by another product.`);
      }
    }

    const productPatch = {};
    let plainFields = {};

    for (const { column, value } of otherEdits) {
      if (column.type === 'shopify-category') {
        const categoryId = SHOPIFY_CATEGORY_OPTIONS[value] || '';
        const nameParts = value.split(' > ');
        const categoryData = { categoryId, fullName: value, name: nameParts[nameParts.length - 1] };
        await saveProductShopifyCategory(customer, product.productUri, categoryData);
        productPatch.shopifyCategory = categoryData;
      } else if (column.type === 'ebay-category') {
        await saveProductEbayCategory(customer, product.productUri, value, product.gptCategories);
        productPatch.gptCategories = { ...(product.gptCategories || {}), primaryCategory: value };
      } else if (column.fieldPath === 'gptDimensionsData') {
        await saveProductDimensions(customer, product.productUri, value);
        productPatch.gptDimensionsData = value;
        // Shopify/eBay/Etsy posts read the "<value> <unit>" copies in
        // consolidatedData, not gptDimensionsData — keep both in step, in the
        // same shape cv_pipeline_node writes.
        const withUnit = (v, unit) => (v ? `${v} ${unit}` : '');
        specEdits['Item Length'] = withUnit(value.length, value.dimensionsUnit);
        specEdits['Item Width'] = withUnit(value.width, value.dimensionsUnit);
        specEdits['Item Height'] = withUnit(value.height, value.dimensionsUnit);
        specEdits['Item Weight'] = withUnit(value.weight, value.weightUnit);
      } else if (column.fieldPath === 'quantity') {
        const qty = Math.max(0, parseInt(value, 10) || 0);
        await saveProductQuantity(customer, product.productUri, qty);
        productPatch.quantity = qty;
      } else {
        // sku, upc, location and any other plain field — blank clears it.
        const raw = column.fieldPath === 'sku' ? sku : value;
        const v = raw === '' || raw == null ? null : raw;
        plainFields = mergeDeep(plainFields, nestedPatch(column.fieldPath, v));
        const root = column.fieldPath.split('.')[0];
        productPatch[root] = setFieldValue({ ...product, ...productPatch }, column.fieldPath, v)[root];
      }
    }

    let updatedSpecs = product.specs;
    if (Object.keys(specEdits).length > 0) {
      updatedSpecs = { ...(product.specs || {}), ...(dirtySpecs[product.productUri] || {}), ...specEdits };
      await saveProductSpecs(customer, product.productUri, updatedSpecs);
      productPatch.specs = updatedSpecs;
    }

    // Always written, even when empty, so `updated` gets stamped like iOS does.
    await saveProductFields(customer, product.productUri, plainFields);
    productPatch.updated = formatStoredDate();

    const html = generateBodyHtml(
      updatedSpecs || {},
      productPatch.gptDimensionsData ?? product.gptDimensionsData,
      product.priceOptions,
      getBodyHtmlTemplate(product),
      productPatch.shopifyCategory?.fullName ?? product.shopifyCategory?.fullName ?? null,
    );
    await saveBodyHtml(customer, product.productUri, html);
    productPatch.shopifyBodyHtml = html;

    if (Object.keys(specEdits).length > 0) {
      setDirtySpecs((prev) => ({ ...prev, [product.productUri]: updatedSpecs }));
    }
    return { patch: productPatch, priceChanged: specEdits.Price !== undefined };
  }, [customer, dirtySpecs, getBodyHtmlTemplate]);

  // A new price shouldn't just sit in Firestore until someone clicks Update on
  // every channel — push it to wherever the item is already listed (iOS
  // syncToConnectedChannels). Best effort; failures surface as a toast.
  const syncPriceToChannels = useCallback(async (product) => {
    const channels = POSTABLE_CHANNELS.filter((c) => isListedOnChannel(product, c) && !(c === 'square' && product.importedFromSquare));
    if (channels.length === 0) return [];
    const apiKey = await getApiKey(customer);
    const failed = [];
    await Promise.all(channels.map((c) => updateProductChannel(apiKey, product.productUri, c, {}).catch((err) => {
      console.error(`Failed to sync price to ${c}:`, err);
      failed.push(channelLabel(c));
    })));
    return failed;
  }, [customer]);

  const handleSaveAll = useCallback(async () => {
    const entries = Object.entries(pendingEdits);
    if (entries.length === 0) return;
    setSavingAll(true);
    const failures = [];
    const syncFailures = [];
    for (const [uri, { product: snapshot, fields }] of entries) {
      // Prefer the freshest copy (e.g. after a regenerate or channel post since
      // the edit) so saving the specs map doesn't write back stale values.
      const product = products.find((p) => p.productUri === uri) || snapshot;
      setSavingUris((prev) => new Set(prev).add(uri));
      try {
        const { patch, priceChanged } = await saveProductEdits(product, fields);
        setProducts((prev) => prev.map((p) => p.productUri === uri ? { ...p, ...patch } : p));
        handleDiscardEdits(uri);
        // A grading change re-prices the card for its new grade on the
        // backend (regenerate_price_node reads the just-saved specs), unless
        // the seller set a price in the same save.
        const gradeEdited = Object.keys(fields).some((path) => GRADE_REPRICE_FIELDS.includes(path.replace(/^consolidatedData\./, '')));
        if (gradeEdited && !priceChanged) {
          try {
            const apiKey = await getApiKey(customer);
            await regenerateProduct(apiKey, uri, 'price');
            const refreshed = await refreshProduct(uri);
            if (refreshed) {
              const failedChannels = await syncPriceToChannels(refreshed);
              if (failedChannels.length) syncFailures.push(...failedChannels);
            }
          } catch (err) {
            console.error('Re-pricing after grade change failed:', err);
            failures.push(`${product.specs?.Title || product.title || 'Untitled'}: saved, but couldn't re-price for the new grade — use Regenerate price`);
          }
        }
        if (priceChanged) {
          const failedChannels = await syncPriceToChannels({ ...product, ...patch });
          if (failedChannels.length) syncFailures.push(...failedChannels);
        }
      } catch (err) {
        console.error('Failed to save product changes:', err);
        failures.push(`${product.specs?.Title || product.title || 'Untitled'}: ${err.message}`);
      } finally {
        setSavingUris((prev) => {
          const next = new Set(prev);
          next.delete(uri);
          return next;
        });
      }
    }
    setSavingAll(false);
    if (failures.length) {
      setToast({ severity: 'error', message: `Couldn't save ${failures.length} item${failures.length === 1 ? '' : 's'} — ${failures.join(' · ')}` });
    } else if (syncFailures.length) {
      setToast({ severity: 'warning', message: `Saved, but the new price didn't sync to ${[...new Set(syncFailures)].join(', ')} — use Update on that channel.` });
    } else {
      setToast({ severity: 'success', message: 'Changes saved' });
    }
  }, [pendingEdits, products, saveProductEdits, syncPriceToChannels, handleDiscardEdits, customer, refreshProduct]);

  const pendingChangeCount = useMemo(
    () => Object.values(pendingEdits).reduce((n, e) => n + Object.keys(e.fields).length, 0),
    [pendingEdits]
  );
  const hasUnsavedEdits = pendingChangeCount > 0;

  // Warn before a reload/tab close or an in-app navigation drops unsaved edits.
  useEffect(() => {
    if (!hasUnsavedEdits) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedEdits]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => (
    hasUnsavedEdits && currentLocation.pathname !== nextLocation.pathname
  ));
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('You have unsaved changes. Leave this page and discard them?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  // ── Regenerate (synchronous — POST /api/product/{id}/regenerate) ───────────
  const handleRegenerate = useCallback(async (product, field) => {
    setRegeneratingUris((prev) => new Set(prev).add(product.productUri));
    try {
      const apiKey = await getApiKey(customer);
      await regenerateProduct(apiKey, product.productUri, field);
      // The backend persists the new values itself; re-read so every
      // regenerated field (incl. item specifics for "all") shows up.
      await refreshProduct(product.productUri);
      setToast({ severity: 'success', message: 'Regenerated' });
    } catch (err) {
      console.error('Regenerate failed:', err);
      setToast({ severity: 'error', message: channelErrorText(err, "Couldn't regenerate — try again") });
    } finally {
      setRegeneratingUris((prev) => {
        const next = new Set(prev);
        next.delete(product.productUri);
        return next;
      });
    }
  }, [customer, refreshProduct]);

  const handleCopy = useCallback((product) => {
    navigator.clipboard.writeText(buildClipboardText(product))
      .then(() => setToast({ severity: 'success', message: 'Copied to clipboard' }))
      .catch(() => setToast({ severity: 'error', message: "Couldn't copy to clipboard" }));
  }, []);

  // ── Post to channel ─────────────────────────────────────────────────────────
  const channelActionErrorMessage = (err, verb, channel) => (
    channelErrorText(err, `Failed to ${verb} ${channelLabel(channel)}: ${err.message}`)
  );

  const _setPending = (productUri, channel, on) => {
    const key = `${productUri}:${channel}`;
    setPendingChannels((prev) => {
      const next = new Set(prev);
      on ? next.add(key) : next.delete(key);
      return next;
    });
  };

  const handlePostToChannel = useCallback(async (product, channel) => {
    if (channel === 'etsy') {
      // Etsy rejects listings without these with an opaque "IDs must be >= 1" 400.
      const missing = missingEtsySettings(customerData, product);
      if (missing.length) {
        setChannelError(`Set your Etsy ${missing.join(', ')} in Settings first.`);
        return;
      }
    }
    if (channel === 'ebay') {
      const title = product.specs?.Title || product.title || '';
      if (title.length > 80 && !window.confirm(
        `eBay requires a title of 80 characters or fewer (this one is ${title.length}). Click OK for us to truncate it for you, or Cancel to edit it yourself.`
      )) return;
    }
    _setPending(product.productUri, channel, true);
    try {
      const apiKey = await getApiKey(customer);
      await postProductToChannel(apiKey, product.productUri, channel, {});
      await refreshProduct(product.productUri);
    } catch (err) {
      console.error(`Failed to post to ${channel}:`, err);
      const missingFields = channel === 'ebay' ? missingRequiredEbayFields(channelErrorText(err, '')) : [];
      if (missingFields.length) {
        setMissingEbay({ product, fields: missingFields });
      } else {
        setChannelError(channelActionErrorMessage(err, 'post to', channel));
      }
    } finally {
      _setPending(product.productUri, channel, false);
    }
  }, [customer, customerData, refreshProduct]);

  // From the List Item dialog / row menu: post to each newly-selected channel.
  const handlePostToChannels = useCallback(async (product, channels) => {
    for (const channel of channels) {
      await handlePostToChannel(product, channel);
    }
  }, [handlePostToChannel]);

  // Saves the eBay item specifics the user filled in after a rejected post
  // (onto consolidatedData + fieldsRequired so they stick), then retries.
  const handleMissingEbaySubmit = useCallback(async (values) => {
    const { product } = missingEbay;
    setMissingEbay(null);
    try {
      const specs = { ...(product.specs || {}), ...(dirtySpecs[product.productUri] || {}), ...values };
      const fieldsRequired = Array.from(new Set([...(product.fieldsRequired || []), ...Object.keys(values)]));
      await saveProductSpecs(customer, product.productUri, specs);
      await saveProductFields(customer, product.productUri, { fieldsRequired });
      const updated = { ...product, specs, fieldsRequired };
      setProducts((prev) => prev.map((p) => p.productUri === product.productUri ? { ...p, specs, fieldsRequired } : p));
      await handlePostToChannel(updated, 'ebay');
    } catch (err) {
      console.error('Failed to save eBay fields:', err);
      setChannelError(channelActionErrorMessage(err, 'post to', 'ebay'));
    }
  }, [missingEbay, dirtySpecs, customer, handlePostToChannel]);

  const handleUpdateChannel = useCallback(async (product, channel) => {
    _setPending(product.productUri, channel, true);
    try {
      const apiKey = await getApiKey(customer);
      await updateProductChannel(apiKey, product.productUri, channel, {});
      await refreshProduct(product.productUri);
    } catch (err) {
      console.error(`Failed to update ${channel} listing:`, err);
      setChannelError(channelActionErrorMessage(err, 'update', channel));
    } finally {
      _setPending(product.productUri, channel, false);
    }
  }, [customer, refreshProduct]);

  const _setEnhancing = (productUri, on) => {
    setEnhancingProducts((prev) => {
      const next = new Set(prev);
      on ? next.add(productUri) : next.delete(productUri);
      return next;
    });
  };

  // Enhancement runs async on the backend, so we poll the product doc until
  // it comes back ready (or we give up) instead of assuming one refresh is enough.
  const pollForEnhancement = useCallback((productUri, attemptsLeft) => {
    setTimeout(() => {
      getProducts(1, null, customer, '', ([updated]) => {
        if (!updated) {
          _setEnhancing(productUri, false);
          return;
        }
        setProducts((prev) => prev.map((p) => p.productUri === updated.productUri ? updated : p));
        if (updated.productReady || attemptsLeft <= 1) {
          _setEnhancing(productUri, false);
        } else {
          pollForEnhancement(productUri, attemptsLeft - 1);
        }
      }, false, { productUri });
    }, 10000);
  }, [customer]);

  const handleEnhanceWithAI = useCallback(async (product) => {
    _setEnhancing(product.productUri, true);
    try {
      const apiKey = await getApiKey(customer);
      await retryProduct(apiKey, product.productUri, 0, '');
    } catch (err) {
      console.error('Failed to enhance product:', err);
      _setEnhancing(product.productUri, false);
      return;
    }
    // ~5 minutes of polling at 10s intervals before giving up on the spinner
    pollForEnhancement(product.productUri, 30);
  }, [customer, pollForEnhancement]);

  const handleDeleteFromChannel = useCallback(async (product, channel) => {
    try {
      const apiKey = await getApiKey(customer);
      await deleteProductFromChannel(apiKey, product.productUri, channel);
      await refreshProduct(product.productUri);
    } catch (err) {
      console.error(`Failed to delete from ${channel}:`, err);
      setChannelError(channelActionErrorMessage(err, 'delete from', channel));
    }
  }, [customer, refreshProduct]);

  // ── Column visibility toggle ────────────────────────────────────────────────
  const handleVisibilityToggle = useCallback((colId) => {
    if (!localConfig) return;
    const updated = {
      ...localConfig,
      columns: localConfig.columns.map((c) =>
        c.id === colId ? { ...c, visible: !(c.visible !== false) } : c
      ),
    };
    setLocalConfig(updated);
    saveConfig(updated);
  }, [localConfig, saveConfig]);

  // ── Column reorder (table view header drag) ─────────────────────────────────
  const handleColumnMove = useCallback((fromIndex, toIndex) => {
    if (!localConfig) return;
    const allCols = [...localConfig.columns];
    const from = allCols.findIndex((c) => c.id === visibleColumns[fromIndex]?.id);
    const to = allCols.findIndex((c) => c.id === visibleColumns[toIndex]?.id);
    if (from === -1 || to === -1) return;
    const [moved] = allCols.splice(from, 1);
    allCols.splice(to, 0, moved);
    const updated = { ...localConfig, columns: allCols };
    setLocalConfig(updated);
    saveConfig(updated);
  }, [localConfig, visibleColumns, saveConfig]);

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = useCallback(async (format) => {
    const fmt = (s) => s.replaceAll('-', '');
    try {
      const apiKey = await getApiKey(customer);
      const res = await fetch(
        `${PRODUCTS_ENDPOINT}?start_date=${fmt(startDate)}&end_date=${fmt(endDate)}&output_format=${format}`,
        { headers: { 'x-api-key': apiKey, Accept: format === 'csv' ? 'text/csv' : 'application/json' } }
      );
      if (!res.ok) throw new Error(res.statusText);
      const isGzip = res.headers.get('Content-Encoding') === 'gzip';
      const buf = await (await res.blob()).arrayBuffer();
      const text = (format === 'csv' && isGzip)
        ? pako.inflate(new Uint8Array(buf), { to: 'string' })
        : new TextDecoder().decode(buf);
      const url = URL.createObjectURL(new Blob([text], { type: format === 'csv' ? 'text/csv' : 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = `products_${fmt(startDate)}_${fmt(endDate)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setExportAnchorEl(null);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  }, [customer, startDate, endDate]);

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    try {
      if (deleteEverywhere) {
        const apiKey = await getApiKey(customer);
        const channels = POSTABLE_CHANNELS.filter((c) => isListedOnChannel(productToDelete, c));
        for (const channel of channels) {
          try {
            await deleteProductFromChannel(apiKey, productToDelete.productUri, channel);
          } catch (err) {
            console.error(`Failed to delete from ${channel}:`, err);
          }
        }
      }
      await deleteProduct(customer, productToDelete.productUri);
      handleDiscardEdits(productToDelete.productUri);
      setProducts((prev) => prev.filter((p) => p.productUri !== productToDelete.productUri));
      setTotalItems((n) => Math.max(n - 1, 0));
    } catch (err) {
      alert('Failed to delete product.');
    } finally {
      setConfirmOpen(false);
      setProductToDelete(null);
      setDeleteEverywhere(false);
    }
  }, [customer, productToDelete, deleteEverywhere, handleDiscardEdits]);

  // Per-product view state shared by the grid cards and the table rows.
  const getRowState = (product) => {
    const mergedSpecs = { ...(product.specs || {}), ...(dirtySpecs[product.productUri] || {}) };
    const edits = pendingEdits[product.productUri]?.fields || {};
    const hasPendingEdits = Object.keys(edits).length > 0;
    const isSaving = savingUris.has(product.productUri);
    const mergedProduct = Object.values(edits).reduce(
      (p, { column, value }) => setFieldValue(p, column.fieldPath, value),
      { ...product, specs: mergedSpecs }
    );
    const isEnhancing = enhancingProducts.has(product.productUri);
    const isExpanded = expandedUris.has(product.productUri);

    const allImages = imageCol ? (getFieldValue(mergedProduct, imageCol.fieldPath) || []) : [];
    const imageUrl = allImages[0] || null;
    // consolidatedData.Title only lands once finalization fully completes, but
    // validatedTitle is written much earlier (once CV processing finishes) and
    // holds the same value once finalization catches up — use it to show a real
    // title sooner instead of leaving the card on a spinner the whole time.
    const finalTitle = titleCol ? getFieldValue(mergedProduct, titleCol.fieldPath) : product.title;
    const titleValue = finalTitle || mergedProduct.validatedTitle;
    const priceValue = priceCol ? getFieldValue(mergedProduct, priceCol.fieldPath) : null;
    // The sku column can point at a fieldPath that only resolves once
    // finalization writes it — fall back to the sku assigned at creation
    // so the badge never goes blank while that's still in flight.
    const skuValue = (skuCol ? getFieldValue(mergedProduct, skuCol.fieldPath) : null) || mergedProduct.sku || product.sku;
    const category = mergedProduct.specs?.Category;
    const descriptionValue = mergedProduct.specs?.Description;
    const isSoldOut = product.quantity === 0;
    // Title/price each show their own "generating…" spinner until
    // populated — don't offer AI enhancement while either is still
    // in flight, even if the backend has flagged finishedProcessing early.
    // Category is slow to load, so it's shown only in the expanded
    // section (below) and doesn't gate this.
    const stillGeneratingCore = !product.productReady && (!titleValue || priceValue == null || priceValue === '');
    const needsEnhancement = product.finishedProcessing && !product.productReady && !product.needsImages && !stillGeneratingCore;
    // Stuck in processing with no title well after upload/retry — offer a full retry.
    const hasFailed = didFailProcessing(product, titleValue);

    return {
      edits, hasPendingEdits, isSaving, mergedProduct, isEnhancing, isExpanded,
      allImages, imageUrl, titleValue, priceValue, skuValue, category, descriptionValue,
      isSoldOut, needsEnhancement, hasFailed,
    };
  };

  // Shared cell wiring for the table rows and the expanded-card field rows.
  const renderCell = (product, mergedProduct, edits, col) => {
    const channel = POSTABLE_CHANNELS.includes(col.id) ? col.id : null;
    return (
      <InlineEditCell
        key={col.id}
        column={col}
        value={getFieldValue(mergedProduct, col.fieldPath)}
        product={mergedProduct}
        onSave={(val) => handleFieldEdit(product, col, val)}
        dirty={!!edits[col.fieldPath]}
        lockedReason={col.id === 'sku' && isListedOnChannel(product, 'ebay') ? 'SKU cannot be edited after posting to eBay.' : null}
        onClick={col.id === 'bodyHtml' ? () => setDrawerProduct(mergedProduct) : undefined}
        onPost={channel ? () => handlePostToChannel(product, channel) : undefined}
        onUpdate={channel ? () => handleUpdateChannel(product, channel) : undefined}
        onDelete={channel ? () => handleDeleteFromChannel(product, channel) : undefined}
        isPending={pendingChannels.has(`${product.productUri}:${col.id}`)}
      />
    );
  };

  // Enhance / retry / processing indicators shown next to a product's actions.
  const renderStatusActions = (product, { needsEnhancement, isEnhancing, hasFailed }) => (
    <>
      {(needsEnhancement || hasFailed) && (
        isEnhancing ? (
          <Tooltip title="Processing with AI — this can take a few minutes">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30 }}>
              <CircularProgress size={16} />
            </Box>
          </Tooltip>
        ) : hasFailed ? (
          <Tooltip title="Item failed to process — retry">
            <IconButton size="small" color="error" onClick={() => handleEnhanceWithAI(product)}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Incomplete for cross-listing — enhance with AI">
            <IconButton size="small" color="primary" onClick={() => handleEnhanceWithAI(product)}>
              <AutoFixHighIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      )}
      {product.needsImages && (
        <Tooltip title="Add images to enable AI enhancement">
          <AutoFixHighIcon fontSize="small" sx={{ color: 'text.disabled', m: '4px' }} />
        </Tooltip>
      )}
    </>
  );

  const renderRowMenuButton = (product) => (
    <Tooltip title="More actions">
      <IconButton size="small" onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, product })}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderUndoButton = (product, isSaving) => (
    <Tooltip title="Discard this item's changes">
      <span>
        <IconButton size="small" disabled={isSaving} onClick={() => handleDiscardEdits(product.productUri)}>
          {isSaving ? <CircularProgress size={16} /> : <UndoIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );

  // "N items · $X total" for the selected segment, from the backend-maintained
  // customer totals when available, else summed over the loaded products
  // (same fallback as iOS ProductListDataModel.clientComputedTotals).
  const inventorySummary = (() => {
    if (activeFilterCount > 0 || searchQuery) return `${totalItems} matching`;
    const t = inventoryTotals || {};
    const loaded = (() => {
      const inSegment = products.filter((p) => (p.quantity ?? 1) > 0
        && (segment === 'all' || (segment === 'listed') === isListedAnywhere(p)));
      return {
        count: inSegment.length,
        value: inSegment.reduce((n, p) => n + (parsePriceValue(p.specs?.Price ?? p.price) || 0), 0),
      };
    })();
    let count;
    let value;
    if (segment === 'all') {
      count = t.itemCount ?? totalItems;
      value = t.unsoldValue ?? loaded.value;
    } else if (segment === 'listed') {
      count = t.listedItemCount ?? loaded.count;
      value = t.listedUnsoldValue ?? loaded.value;
    } else if (t.itemCount != null && t.listedItemCount != null) {
      count = t.itemCount - t.listedItemCount;
      value = (t.unsoldValue ?? 0) - (t.listedUnsoldValue ?? 0);
    } else {
      count = loaded.count;
      value = loaded.value;
    }
    return `${count} item${count === 1 ? '' : 's'} · ${formatCurrency(value, { whole: true })} total`;
  })();

  const priceColumn = priceCol || { id: 'price', label: 'Price', fieldPath: 'consolidatedData.Price', type: 'currency' };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (configLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, pb: hasUnsavedEdits ? 10 : undefined, minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Products</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/uploader')}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          Add Item
        </Button>
      </Box>

      {/* Segments + totals */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={segment}
          onChange={(_, next) => next && setSegment(next)}
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, textTransform: 'none', fontWeight: 600 } }}
        >
          {SEGMENTS.map((seg) => <ToggleButton key={seg.key} value={seg.key}>{seg.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{inventorySummary}</Typography>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search title or SKU…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: { xs: '1 1 100%', sm: '0 1 280px' }, order: { xs: -1, sm: 0 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <Box sx={{ flex: 1 }} />

        {isDesktop && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={handleViewModeChange}
            sx={{ mr: 0.5, '& .MuiToggleButton-root': { px: 1, py: 0.5 } }}
          >
            <ToggleButton value="grid" aria-label="Grid view">
              <Tooltip title="Grid view"><GridViewIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table view"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <Tooltip title={sortDir === 'desc' ? 'Sorted: newest first' : 'Sorted: oldest first'}>
          <IconButton size="small" onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}>
            {sortDir === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* Filter button */}
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => setFilterDialogOpen(true)}
            color={activeFilterCount > 0 ? 'primary' : 'default'}>
            <Badge badgeContent={activeFilterCount || null} color="primary">
              <FilterListIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Export button */}
        <Tooltip title="Export">
          <IconButton size="small" onClick={(e) => setExportAnchorEl(e.currentTarget)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <ColumnVisibilityMenu columns={localConfig?.columns || []} onToggle={handleVisibilityToggle} />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : isTableView ? (
        <DndProvider backend={HTML5Backend}>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', width: '100%', maxHeight: 'calc(100vh - 260px)' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.50', width: 110 }} />
                  {visibleColumns.map((col, idx) => (
                    <DraggableColumnHeader key={col.id} index={idx} column={col} onMove={handleColumnMove}>
                      {col.label}
                    </DraggableColumnHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => {
                  const rowState = getRowState(product);
                  const { edits, hasPendingEdits, isSaving, mergedProduct, isEnhancing } = rowState;
                  return (
                    <TableRow
                      key={product.productUri}
                      hover
                      sx={{
                        opacity: isEnhancing ? 0.6 : 1,
                        ...(hasPendingEdits && { bgcolor: 'rgba(255, 167, 38, 0.05)' }),
                      }}
                    >
                      <TableCell sx={{ p: 0.5, width: 110, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {renderRowMenuButton(product)}
                          {hasPendingEdits && renderUndoButton(product, isSaving)}
                          {renderStatusActions(product, rowState)}
                        </Box>
                      </TableCell>
                      {visibleColumns.map((col) => renderCell(product, mergedProduct, edits, col))}
                    </TableRow>
                  );
                })}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      {hasActiveFilter ? 'No products found.' : 'No products yet — upload some products to get started.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DndProvider>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 1.5 }}>
          {products.map((product) => {
            const rowState = getRowState(product);
            const {
              edits, hasPendingEdits, isSaving, mergedProduct, isEnhancing, isExpanded,
              allImages, imageUrl, titleValue, priceValue, skuValue, category, descriptionValue,
              isSoldOut,
            } = rowState;

            return (
              <Paper
                key={product.productUri}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  opacity: isEnhancing ? 0.6 : 1,
                  gridColumn: isExpanded ? '1 / -1' : 'auto',
                  ...(hasPendingEdits && { borderColor: 'warning.main' }),
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.25, cursor: 'pointer' }}
                  onClick={() => toggleExpand(product.productUri)}
                >
                  {imageUrl ? (
                    <Box component="img" src={imageUrl} alt="" sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ width: 56, height: 56, borderRadius: 1.5, bgcolor: 'grey.100', flexShrink: 0 }} />
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {titleValue ? (
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
                        {titleValue}
                      </Typography>
                    ) : product.productReady ? (
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
                        Untitled
                      </Typography>
                    ) : rowState.hasFailed ? (
                      <Typography variant="caption" color="error">Item failed to process</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircularProgress size={10} />
                        <Typography variant="caption" color="text.disabled">Generating title…</Typography>
                      </Box>
                    )}
                    {skuValue ? (
                      <Chip
                        label={`SKU: ${skuValue}`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', mt: 0.25, bgcolor: 'grey.100' }}
                      />
                    ) : null}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                      {priceValue != null && priceValue !== '' ? (
                        <Typography variant="body2" color="text.secondary">
                          ${String(priceValue).replace(/^\$/, '')}
                        </Typography>
                      ) : !product.productReady && !rowState.hasFailed && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CircularProgress size={10} />
                          <Typography variant="caption" color="text.disabled">Pricing…</Typography>
                        </Box>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                      {isSoldOut && <Chip label="Sold" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      {hasPendingEdits && <Chip label="Unsaved" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      {hasShopify && <ChannelBadge label="Shopify" active={isListedOnChannel(product, 'shopify')} />}
                      {hasEbay && <ChannelBadge label="eBay" active={isListedOnChannel(product, 'ebay')} />}
                      {hasSquare && <ChannelBadge label="Square" active={isListedOnChannel(product, 'square')} />}
                      {hasEtsy && <ChannelBadge label="Etsy" active={isListedOnChannel(product, 'etsy')} />}
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    {hasPendingEdits && renderUndoButton(product, isSaving)}
                    {renderStatusActions(product, rowState)}
                    {renderRowMenuButton(product)}
                    <IconButton size="small" onClick={() => toggleExpand(product.productUri)}>
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <DetailToolbar
                    isRegenerating={regeneratingUris.has(product.productUri)}
                    disabled={!product.productReady}
                    onRegenerate={(field) => handleRegenerate(product, field)}
                    onCopy={() => handleCopy(mergedProduct)}
                  />
                  {descriptionValue ? (
                    <Box sx={{ px: 1.25, pt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {descriptionValue.replace(/<br\s*\/?>/gi, '\n')}
                      </Typography>
                    </Box>
                  ) : null}
                  <Box sx={{ px: 1.25, pt: 1 }}>
                    {category ? (
                      <Typography variant="caption" color="text.disabled">Category: {category}</Typography>
                    ) : !product.productReady && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircularProgress size={10} />
                        <Typography variant="caption" color="text.disabled">Categorizing…</Typography>
                      </Box>
                    )}
                  </Box>
                  <MarketValueRow
                    product={mergedProduct}
                    dirty={!!edits[priceColumn.fieldPath]}
                    onOpen={() => setPriceDialogProduct(product)}
                  />
                  {mergedProduct.mainImages?.length > 0 ? (
                    <ProductPhotoManager
                      images={mergedProduct.mainImages}
                      customer={customer}
                      productUri={product.productUri}
                      onSaved={(newMainImages) => {
                        const newThumbnailUrls = newMainImages.filter((img) => !img.isHidden).map((img) => img.urlString);
                        setProducts((prev) => prev.map((p) => p.productUri === product.productUri
                          ? { ...p, mainImages: newMainImages, thumbnailUrls: newThumbnailUrls, mainImage: newThumbnailUrls[0] || p.mainImage }
                          : p));
                      }}
                    />
                  ) : allImages.length > 0 && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                        gap: 1,
                        p: 1.25,
                      }}
                    >
                      {allImages.map((src, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={src}
                          alt=""
                          sx={{ width: '100%', aspectRatio: '1 / 1', borderRadius: 1.5, objectFit: 'cover' }}
                        />
                      ))}
                    </Box>
                  )}
                  <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
                    <TableBody>
                      {detailColumns.map((col) => (
                        <TableRow key={col.id}>
                          <TableCell sx={{ width: { xs: 96, sm: 150 }, color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, verticalAlign: 'top', pt: 1.25 }}>
                            {col.label}
                          </TableCell>
                          {renderCell(product, mergedProduct, edits, col)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <OtherFieldsSection
                    mergedProduct={mergedProduct}
                    edits={edits}
                    coveredFieldPaths={coveredFieldPaths}
                    onFieldEdit={(col, val) => handleFieldEdit(product, col, val)}
                  />
                  <Divider />
                  <ListingActionsSection
                    product={mergedProduct}
                    onPreview={() => setDrawerProduct(mergedProduct)}
                    onManageListings={() => setListingDialogProduct(product)}
                    onMarkSold={() => setSoldDialogProduct(product)}
                  />
                </Collapse>
              </Paper>
            );
          })}

          {products.length === 0 && (
            hasActiveFilter ? (
              <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No products found.
              </Box>
            ) : (
              <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <Typography sx={{ mb: 1, fontWeight: 600 }} color="text.primary">
                  No products yet
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload some products to get started.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/uploader')}>
                  Upload Products
                </Button>
              </Box>
            )
          )}
        </Box>
      )}

      {/* Pagination + page size */}
      {!hasActiveFilter && totalItems > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
          <Pagination
            count={Math.ceil(totalItems / pageSize)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Per page</InputLabel>
            <Select value={pageSize} label="Per page" onChange={handlePageSizeChange}>
              {PAGE_SIZE_OPTIONS.map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        fields={filterFields}
        filters={filters}
        emptyFilters={EMPTY_FILTERS}
        onApply={setFilters}
      />

      {/* Export Popover */}
      <Popover
        open={exportOpen}
        anchorEl={exportAnchorEl}
        onClose={() => setExportAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Export products</Typography>
          <TextField label="Start date" type="date" size="small" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End date" type="date" size="small" value={endDate}
            onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" fullWidth onClick={() => handleExport('csv')}>
              CSV
            </Button>
            <Button size="small" variant="outlined" fullWidth onClick={() => handleExport('json')}>
              JSON
            </Button>
          </Stack>
        </Box>
      </Popover>

      {/* Body HTML Drawer */}
      <BodyHtmlDrawer
        open={!!drawerProduct}
        onClose={() => setDrawerProduct(null)}
        product={drawerProduct}
        template={drawerProduct ? getBodyHtmlTemplate(drawerProduct) : null}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#111111' }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{productToDelete?.title || 'this product'}</strong>? This cannot be undone.
          </Typography>
          {POSTABLE_CHANNELS.some((c) => isListedOnChannel(productToDelete, c)) ? (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={deleteEverywhere}
                  onChange={(e) => setDeleteEverywhere(e.target.checked)}
                />
              }
              label="Delete everywhere (eBay, Shopify, Etsy, etc)"
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{ backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row actions menu */}
      <Menu
        anchorEl={rowMenu?.anchorEl}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenu && POSTABLE_CHANNELS
          .filter((c) => hasChannel[c] && !isListedOnChannel(rowMenu.product, c) && !(c === 'square' && rowMenu.product.importedFromSquare))
          .map((c) => (
            <MenuItem key={c} onClick={() => { const p = rowMenu.product; setRowMenu(null); handlePostToChannel(p, c); }}>
              <ListItemIcon><StorefrontIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={`List on ${channelLabel(c)}`} />
            </MenuItem>
          ))}
        {rowMenu && (
          <MenuItem onClick={() => { setListingDialogProduct(rowMenu.product); setRowMenu(null); }}>
            <ListItemIcon><StorefrontIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={isListedAnywhere(rowMenu.product) ? 'Manage listings…' : 'List item…'} />
          </MenuItem>
        )}
        {rowMenu && (
          <MenuItem
            disabled={(rowMenu.product.quantity ?? 1) <= 0}
            onClick={() => { setSoldDialogProduct(rowMenu.product); setRowMenu(null); }}
          >
            <ListItemIcon><VerifiedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Mark as sold" />
          </MenuItem>
        )}
        {rowMenu && (
          <MenuItem
            onClick={() => {
              setProductToDelete(rowMenu.product);
              setDeleteEverywhere(false);
              setConfirmOpen(true);
              setRowMenu(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        )}
      </Menu>

      <PriceConditionDialog
        open={!!priceDialogProduct}
        onClose={() => setPriceDialogProduct(null)}
        product={priceDialogProduct ? getRowState(priceDialogProduct).mergedProduct : null}
        onDone={(value) => handleFieldEdit(priceDialogProduct, priceColumn, value)}
      />

      <MarkAsSoldDialog
        open={!!soldDialogProduct}
        onClose={() => setSoldDialogProduct(null)}
        customer={customer}
        product={soldDialogProduct}
        onSold={(patch) => {
          const uri = soldDialogProduct.productUri;
          setProducts((prev) => prev.map((p) => p.productUri === uri ? { ...p, ...patch } : p));
          setToast({ severity: 'success', message: patch.quantity === 0 ? 'Sale recorded — sold out and delisted' : 'Sale recorded' });
          if (patch.quantity === 0) refreshProduct(uri);
        }}
      />

      <ListingChannelsDialog
        open={!!listingDialogProduct}
        onClose={() => setListingDialogProduct(null)}
        customerData={customerData}
        product={listingDialogProduct}
        onList={(channels) => handlePostToChannels(listingDialogProduct, [...channels])}
      />

      <MissingEbayFieldsDialog
        fields={missingEbay?.fields}
        onClose={() => setMissingEbay(null)}
        onSubmit={handleMissingEbaySubmit}
      />

      <EditBar
        changeCount={pendingChangeCount}
        productCount={Object.keys(pendingEdits).length}
        saving={savingAll}
        onSave={handleSaveAll}
        onDiscard={handleDiscardAll}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 8000 : 3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ maxWidth: 560 }}>{toast.message}</Alert> : <span />}
      </Snackbar>

      {/* Channel Action Error Dialog */}
      <Dialog open={!!channelError} onClose={() => setChannelError(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Channel Action Failed</DialogTitle>
        <DialogContent>
          <Typography>{channelError}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelError(null)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
